import {
  DOCUMENT_KIND_LABELS,
  hasDesiSlot,
  testLabelZpl,
  withDesi,
  type DeskProductLabelJob,
  type DocumentKind,
  type LabelFormat,
  type PaperSize,
  type PrintJob,
} from '@wms/domain';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, errorMessage } from '../api';
import { fmt, timeZone } from '../format';
import { printPdf, printZpl } from '../printing';
import { labelDesks, type LabelDesk } from '../productLabels';
import { store, useSession } from '../session';
import { withTimeout } from '../timeout';
import type { PackingStation, PrinterTarget } from '../types';
import { Modal, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { RotateSwitch } from '../ui/LabelSize';

// The desk's printer computer: one computer at a packing desk is switched on as the desk's printer
// ("Bu bilgisayar PK-01 masasının yazıcısı"). It prints every label of the desk through its own
// driver whatever screen it shows; every other device (the phones on the floor) only lists them.

/** Where a document of a packed order stands (GET /api/wms/print-states, /stations/:code/print-log). */
type PrintState = 'waiting' | 'printed' | 'failed';

interface PrintJobState {
  readonly id: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly stationCode: string;
  readonly kind: DocumentKind;
  readonly format: LabelFormat;
  readonly paper: PaperSize;
  readonly printerType: PrinterTarget['type'];
  readonly state: PrintState;
  /** printed: when it came out; otherwise when it was queued */
  readonly at: string;
  readonly error: string | null;
  /** the parcel is still at the desk and this is its latest print: "Tekrar bas" may send it again */
  readonly reprintable: boolean;
}

/** A desk and the computer that prints its labels (GET /api/wms/desk-printers). */
interface DeskPrinterStatus {
  readonly code: string;
  readonly name: string;
  readonly warehouseId: string;
  readonly browserPrinted: boolean;
  readonly designated: boolean;
  readonly online: boolean;
  readonly seenAt: string | null;
  readonly waiting: number;
  readonly failed: number;
}

const DEVICE_KEY = 'wms.device';
const DESK_KEY = 'wms.deskPrinter';
/** fired on this page when the switch changes */
const DESK_CHANGED = 'wms:desk-printer';
/** fired when a job printed, failed or went back to the queue, or this computer started printing: the lists load again */
const PRINTS_CHANGED = 'wms:prints';

/** the server's heartbeat is checked against 60 s: a beat every 15 s leaves room for a slow one */
const BEAT_MS = 15_000;
/** how often the printer computer looks for new jobs */
const POLL_MS = 3_000;
/** between two labels: the driver gets each job whole before the next */
const GAP_MS = 1_500;
/** a print that never got going (a hidden frame that never loaded) is reported as failed */
const PRINT_TIMEOUT_MS = 60_000;

// a phone has no label printer: the switch is not offered there to the floor
export const phone = () => window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false;

let device: string | null = null;
/** This browser's own id, kept on the computer: the server hands a desk's jobs to the one switched on. */
function deviceId(): string {
  if (device) return device;
  const saved = store.get(DEVICE_KEY);
  device =
    saved && /^[A-Za-z0-9-]{8,64}$/.test(saved)
      ? saved
      : typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  if (device !== saved) store.set(DEVICE_KEY, device);
  return device;
}

// kept in memory too: a kiosk browser without localStorage still prints until it is closed
let desk: string | null = store.get(DESK_KEY);
/** the desk just switched on here: its next heartbeat takes the role from whichever computer had it */
let takeOver: string | null = null;

// the desk switched off or changed is released by the print agent (after its last beat)
function switchDesk(code: string | null): void {
  desk = code;
  takeOver = code;
  store.set(DESK_KEY, code);
  window.dispatchEvent(new Event(DESK_CHANGED));
}

/** The desk this computer prints for, if it was switched on here. */
function useDesk(): string | null {
  const [code, setCode] = useState(desk);
  useEffect(() => {
    const changed = () => setCode(desk);
    // another tab of this browser switched it
    const stored = (e: StorageEvent) => {
      if (e.key !== DESK_KEY) return;
      desk = e.newValue;
      // switched on on purpose there: the tab that prints takes the desk over
      takeOver = e.newValue;
      changed();
    };
    window.addEventListener(DESK_CHANGED, changed);
    window.addEventListener('storage', stored);
    return () => {
      window.removeEventListener(DESK_CHANGED, changed);
      window.removeEventListener('storage', stored);
    };
  }, []);
  return code;
}

/** the desi typed at this desk for an order's label (our labels print none by default) */
const desiByOrder = new Map<string, string>();

/** The desi the packing screen wants written onto the order's label when the desk prints it; null: none. */
export function rememberDesi(orderId: string, desi: string | null): void {
  if (desi) desiByOrder.set(orderId, desi);
  else desiByOrder.delete(orderId);
}

/** Stored print jobs carry ZPL as text and PDFs as base64; our labels get the desi when one was given. */
async function printJob(job: PrintJob): Promise<void> {
  if (job.payload.encoding === 'object') throw new Error('Belge içeriği gelmedi');
  const desi = desiByOrder.get(job.orderId) ?? null;
  const bytes = () => (job.payload.encoding === 'base64' ? Uint8Array.from(atob(job.payload.data), (c) => c.charCodeAt(0)) : new TextEncoder().encode(job.payload.encoding === 'utf8' ? job.payload.data : ''));
  if (job.format === 'ZPL') {
    const zpl = job.payload.encoding === 'utf8' ? job.payload.data : new TextDecoder().decode(bytes());
    await printZpl(desi && job.kind === 'shipping_label' && hasDesiSlot(zpl) ? withDesi(zpl, desi) : zpl, DOCUMENT_KIND_LABELS[job.kind]);
    return;
  }
  await printPdf(new Blob([bytes()], { type: 'application/pdf' }));
}

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * A steady tick that keeps going while the tab is in the background. Browsers throttle a hidden
 * page's timers (Chrome: at most once a second, and once a minute after five minutes hidden); a
 * worker's timer is held back far less, so the ticks come from one when the page may start it.
 * Either way the agent polls only as often as the browser allows.
 */
function ticker(ms: number, onTick: () => void): () => void {
  let fallback: ReturnType<typeof setInterval> | null = null;
  const plain = () => {
    fallback ??= setInterval(onTick, ms);
  };
  let worker: Worker | null = null;
  let url: string | null = null;
  try {
    url = URL.createObjectURL(new Blob([`setInterval(() => postMessage(0), ${ms});`], { type: 'text/javascript' }));
    worker = new Worker(url);
    worker.onmessage = onTick;
    worker.onerror = () => {
      worker?.terminate();
      plain();
    };
  } catch {
    plain();
  }
  return () => {
    worker?.terminate();
    if (url) URL.revokeObjectURL(url);
    if (fallback) clearInterval(fallback);
  };
}

/**
 * The print agent of the computer switched on as a desk's printer: it beats to the server and
 * prints the desk's browser jobs one by one, with a short gap, whatever screen is open and also
 * while the tab is in the background.
 */
function usePrintAgent(code: string | null): void {
  const toast = useToast();
  const say = useRef(toast);
  say.current = toast;

  useEffect(() => {
    if (!code) return;
    let stopped = false;
    let beating = false;
    let printing = false;
    let beatAt = 0;
    // the beat on its way: a release goes after it, or that beat would take the desk back
    let inFlight: Promise<unknown> = Promise.resolve();
    let stopTicker: (() => void) | null = null;
    const me = deviceId();
    const forget = (text: string) => {
      stopped = true;
      if (desk === code) {
        desk = null;
        store.set(DESK_KEY, null);
        window.dispatchEvent(new Event(DESK_CHANGED));
      }
      say.current(text, 'bad');
    };
    // the server out of reach: the next tick tries again (the floor sees the desk offline meanwhile)
    const failed = (e: unknown) => {
      if (e instanceof ApiError && e.status === 404) forget(`${code} masası bulunamadı; bu bilgisayarın yazıcı ayarı kapatıldı.`);
    };

    // beats on its own, so a long run of labels never makes the desk look offline
    const beat = async () => {
      if (stopped || beating || Date.now() - beatAt < BEAT_MS) return;
      beating = true;
      try {
        const sent = api<{ designated: boolean }>('POST', `/api/wms/stations/${code}/printer-heartbeat`, {
          device: me,
          ...(takeOver === code ? { take: true } : {}),
        });
        inFlight = sent.catch(() => undefined);
        const { designated } = await sent;
        if (takeOver === code) takeOver = null;
        const first = beatAt === 0;
        beatAt = Date.now();
        if (!designated) forget(`${code} etiketlerini artık başka bir bilgisayar basıyor; bu bilgisayarda kapatıldı.`);
        else if (first) {
          // this computer's own warning goes at once, and what waits prints
          window.dispatchEvent(new Event(PRINTS_CHANGED));
          void print();
        }
      } catch (e) {
        failed(e);
      } finally {
        beating = false;
      }
    };

    // taken first (only this computer gets the desk's jobs), printed, reported; false: another took it
    const take = async (path: string, out: () => Promise<void>, what: string): Promise<boolean> => {
      const { claimed } = await api<{ claimed: boolean }>('POST', `${path}/claim`, { device: me });
      if (!claimed) return false;
      const ack = (body: object) => api('POST', `${path}/ack`, body);
      let fault: unknown = null;
      try {
        await withTimeout(out(), PRINT_TIMEOUT_MS, 'Yazdırma başlamadı (zaman aşımı)');
      } catch (e) {
        fault = e;
      }
      if (fault === null) {
        // out of the printer: a lost answer is tried once more (unanswered it turns "Basılamadı")
        await ack({ status: 'printed' })
          .catch(() => ack({ status: 'printed' }))
          .catch(() => undefined);
      } else {
        await ack({ status: 'failed', error: errorMessage(fault).slice(0, 1000) }).catch(() => undefined);
        say.current(`${what} basılamadı: ${errorMessage(fault)}`, 'bad');
      }
      return true;
    };

    // one job at a time, the parcels' documents before product labels sent from another screen ("Barkodu bas")
    const print = async () => {
      if (stopped || printing || beatAt === 0) return;
      printing = true;
      try {
        const jobs = await api<PrintJob[]>('GET', `/api/wms/stations/${code}/print-jobs`);
        for (const job of jobs) {
          if (stopped) return;
          if (!job.printerId.startsWith('browser:')) continue;
          if (!(await take(`/api/wms/print-jobs/${job.id}`, () => printJob(job), DOCUMENT_KIND_LABELS[job.kind]))) continue;
          window.dispatchEvent(new Event(PRINTS_CHANGED));
          await pause(GAP_MS);
        }
        const labels = await api<DeskProductLabelJob[]>('GET', `/api/wms/stations/${code}/product-labels`);
        for (const label of labels) {
          if (stopped) return;
          if (!(await take(`/api/wms/product-labels/${label.id}`, () => printZpl(label.zpl, `${label.sku} etiketi`).then(() => undefined), `${label.sku} etiketi`))) continue;
          await pause(GAP_MS);
        }
      } catch (e) {
        failed(e);
      } finally {
        printing = false;
      }
    };

    const tick = () => {
      void beat();
      void print();
    };

    // two tabs of this computer: one prints, the other waits for its turn (without Web Locks both
    // may run; a job still prints once, the server hands it to one of them)
    let release = () => {};
    const held = new Promise<void>((resolve) => (release = resolve));
    const start = () => {
      if (stopped) return undefined;
      stopTicker = ticker(POLL_MS, tick);
      tick();
      return held;
    };
    if (navigator.locks) void navigator.locks.request('wms-desk-printer', start).catch(() => undefined);
    else void start();
    return () => {
      stopped = true;
      stopTicker?.();
      release();
      // switched off or to another desk (here or in another tab), not just logged out: the desk is
      // free for another computer; best effort, a computer that cannot say so falls silent
      if (desk !== code) {
        void inFlight.then(() => api('POST', `/api/wms/stations/${code}/printer-heartbeat`, { device: me, release: true })).catch(() => undefined);
      }
    };
  }, [code]);
}

/** Desks of the warehouse and their printer computers, kept current. */
function useDeskPrinters(refreshMs = 15_000) {
  const { warehouseId } = useSession();
  return useLoad(() => api<DeskPrinterStatus[]>('GET', `/api/wms/desk-printers${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], refreshMs);
}

/** Desks a product label can be sent to ("Barkodu bas"), for warehouse staff; none for a seller. */
export function useLabelDesks(): readonly LabelDesk[] {
  const { isOperator, warehouseId } = useSession();
  const desks = useLoad(
    () => (isOperator ? api<DeskPrinterStatus[]>('GET', `/api/wms/desk-printers${warehouseId ? `?warehouseId=${warehouseId}` : ''}`) : Promise.resolve([])),
    [isOperator, warehouseId],
    15_000,
  );
  return labelDesks(desks.data ?? []);
}

/** A desk's prints changed outside this file (labels printed on the picker's device): the lists and warnings load again. */
export const printsChanged = (): void => void window.dispatchEvent(new Event(PRINTS_CHANGED));

/** Reloads when a job printed, failed or went back to the queue on this page. */
function usePrintsChanged(reload: () => void): void {
  const again = useRef(reload);
  again.current = reload;
  useEffect(() => {
    const on = () => again.current();
    window.addEventListener(PRINTS_CHANGED, on);
    return () => window.removeEventListener(PRINTS_CHANGED, on);
  }, []);
}

/**
 * Mounted once in the operator's shell: runs the print agent on the computer switched on as a
 * desk's printer, and warns the packing and picking screens when a desk's printer computer is gone.
 */
export function DeskPrinter({ section }: { section: string }) {
  const code = useDesk();
  usePrintAgent(code);
  // a picker's walk keeps its scan field in view: only the line itself there
  return section === 'paketleme' || section === 'toplama' ? <OfflineWarning brief={section === 'toplama'} /> : null;
}

/** The red line on the packing and picking screens: a desk whose labels are not printing. */
function OfflineWarning({ brief }: { brief: boolean }) {
  const desks = useDeskPrinters();
  usePrintsChanged(() => void desks.reload());
  const down = (desks.data ?? []).filter((d) => d.browserPrinted && !d.online && (d.designated || d.waiting > 0));
  return (
    <>
      {down.map((d) => (
        <div key={d.code} className="banner bad" role="alert">
          <Icon name="printer" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div>{d.code} yazıcı bilgisayarı çevrimdışı — etiketler basılmıyor</div>
            {!brief && (
              <div style={{ fontWeight: 500 }}>
                {d.waiting > 0 ? `${d.waiting} baskı bekliyor. ` : ''}
                {d.designated && d.seenAt
                  ? `Son sinyal ${fmt.age(d.seenAt)} önce. Masadaki bilgisayarda Pronto Depo açık kalmalı.`
                  : `Masadaki bilgisayarda Paketleme ekranından “Bu bilgisayar ${d.code} masasının yazıcısı”nı açın.`}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

/** "Bu bilgisayar PK-01 masasının yazıcısı": stored on this computer, off until someone turns it on. */
function DeskSwitch({ station, status }: { station: Pick<PackingStation, 'code'>; status: DeskPrinterStatus | undefined }) {
  const here = useDesk() === station.code;
  const toggle = (on: boolean) => {
    if (!on) return switchDesk(null);
    // another computer prints this desk now: it is handed over only on purpose
    if (status?.designated && status.online && !window.confirm(`${station.code} etiketlerini şu an başka bir bilgisayar basıyor. Bu bilgisayar devralsın mı?`)) return;
    switchDesk(station.code);
  };
  return (
    <label className={`desk-switch${here ? ' on' : ''}`}>
      <input type="checkbox" checked={here} onChange={(e) => toggle(e.target.checked)} />
      <span>
        <b>Bu bilgisayar {station.code} masasının yazıcısı</b>
        <small className="muted">
          {here
            ? 'Açık: masanın etiketleri hangi ekran açık olursa olsun bu bilgisayardan basılır. Pronto Depo bu bilgisayarda açık kalmalı.'
            : 'Yalnızca etiket yazıcısı bağlı bilgisayarda açın. Telefonda açmayın.'}
        </small>
      </span>
    </label>
  );
}

/** Ayarlar: supervisors switch a computer on as a desk's printer from here too. */
export function DeskPrinterSetting() {
  const { isSupervisor, isOperator, warehouseId } = useSession();
  const desks = useLoad(
    () => (isSupervisor ? api<DeskPrinterStatus[]>('GET', `/api/wms/desk-printers${warehouseId ? `?warehouseId=${warehouseId}` : ''}`) : Promise.resolve([])),
    [isSupervisor, warehouseId],
  );
  const browserDesks = (desks.data ?? []).filter((d) => d.browserPrinted);
  if (!isOperator || !isSupervisor || browserDesks.length === 0) return null;
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="printer" />
        <h2>Paketleme masası yazıcısı</h2>
        <span className="sub">Bu cihazda geçerli</span>
      </div>
      <div className="card-b stack">
        {phone() && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Bu cihaz telefon görünüyor: etiketleri masadaki bilgisayar basmalı.</p>}
        {browserDesks.map((d) => (
          <DeskSwitch key={d.code} station={d} status={d} />
        ))}
      </div>
    </section>
  );
}

// printed today: "14:02"; earlier: "19.10 14:02"
const clock = () => new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: timeZone() });
export const printedAt = (iso: string) => (fmt.isoDay(new Date(iso)) === fmt.today() ? clock().format(new Date(iso)) : fmt.dateTime(iso));

/** "Tekrar bas": back to the desk's queue, its printer computer prints it again. */
function useRequeue() {
  const toast = useToast();
  return async (job: PrintJobState) => {
    try {
      await api('POST', `/api/wms/print-jobs/${job.id}/requeue`);
      toast(`${job.orderNumber}: ${DOCUMENT_KIND_LABELS[job.kind].toLocaleLowerCase('tr')} masanın yazıcısına yeniden gönderildi`, 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
    window.dispatchEvent(new Event(PRINTS_CHANGED));
  };
}

/**
 * A label's state: "Basılmayı bekliyor", "Basıldı 14:02" or "Basılamadı" with "Tekrar bas".
 * again: a printed one can go to the desk's printer once more too (a phone prints nothing itself).
 */
function PrintStateChip({ job, named, again }: { job: PrintJobState; named?: boolean; again?: boolean }) {
  const requeue = useRequeue();
  const name = named ? `${DOCUMENT_KIND_LABELS[job.kind]}: ` : '';
  if (job.state === 'printed') {
    const chip = (
      <span className="chip ok">
        <Icon name="check" size={14} /> {name}Basıldı {printedAt(job.at)}
      </span>
    );
    if (!again || !job.reprintable) return chip;
    return (
      <span className="print-state">
        {chip}
        <button
          className="btn sm"
          onClick={(e) => {
            e.stopPropagation();
            void requeue(job);
          }}
          title="Masanın yazıcısından bir kez daha basar"
        >
          <Icon name="printer" size={16} /> Yeniden bas
        </button>
      </span>
    );
  }
  if (job.state === 'waiting') {
    return (
      <span className="chip warn">
        <Icon name="clock" size={14} /> {name}Basılmayı bekliyor
      </span>
    );
  }
  return (
    <span className="print-state">
      <span className="chip bad" title={job.error ?? undefined}>
        <Icon name="alert" size={14} /> {name}Basılamadı
      </span>
      {job.reprintable && (
        <button
          className="btn sm"
          onClick={(e) => {
            // the row behind opens the order
            e.stopPropagation();
            void requeue(job);
          }}
        >
          <Icon name="printer" size={16} /> Tekrar bas
        </button>
      )}
    </span>
  );
}

/** The label state of packed orders, kept current while the desk prints. */
export function usePrintStates(orderIds: readonly string[]): ReadonlyMap<string, readonly PrintJobState[]> {
  const key = orderIds.join(',');
  const states = useLoad(() => (key ? api<PrintJobState[]>('GET', `/api/wms/print-states?orderIds=${key}`) : Promise.resolve([])), [key], 5_000);
  usePrintsChanged(() => void states.reload());
  const byOrder = new Map<string, PrintJobState[]>();
  for (const s of states.data ?? []) byOrder.set(s.orderId, [...(byOrder.get(s.orderId) ?? []), s]);
  return byOrder;
}

/** The shipping label's state in a list of packed orders ("Paketlenenler"); again: see PrintStateChip. */
export function LabelStateCell({ states, again }: { states: readonly PrintJobState[] | undefined; again?: boolean }) {
  const label = states?.find((s) => s.kind === 'shipping_label') ?? states?.[0];
  return label ? <PrintStateChip job={label} again={again} /> : null;
}

/** A packed order's documents as the desk's printer has them; again: see PrintStateChip. */
export function LabelState({ orderId, again }: { orderId: string; again?: boolean }) {
  const jobs = usePrintStates([orderId]).get(orderId) ?? [];
  return (
    <>
      {jobs.map((j) => (
        <PrintStateChip key={j.id} job={j} named again={again} />
      ))}
    </>
  );
}

const PRINTER_LABELS: Readonly<Record<PrinterTarget['type'], string>> = {
  browser: 'Masadaki bilgisayarın yazıcısı (USB, sürücüyle)',
  raw_tcp: 'Ağ yazıcısı (ZPL, IP adresiyle)',
  agent: 'Yazdırma ajanı',
};

const PRINTER_HELP: Readonly<Record<PrinterTarget['type'], string>> = {
  browser: 'Etiket, masanın yazıcısı olarak açılan bilgisayarda çizilip yazıcı sürücüsüyle basılır. Her termal yazıcıyla çalışır (ör. G&G GG-D1180CW).',
  raw_tcp: 'ZPL doğrudan yazıcının IP adresine gider. Sunucu aynı ağdaysa çalışır; bulutta çalışan sunucu depodaki yazıcıya ulaşamaz.',
  agent: 'Masadaki ayrı bir yazdırma programı işleri çeker.',
};

const STATE_ORDER: Readonly<Record<PrintState, number>> = { failed: 0, waiting: 1, printed: 2 };

/**
 * The desk's printer on the packing screen: who prints it, the switch for this computer, and the
 * desk's jobs (failed ones red with "Tekrar bas", waiting ones, the last printed ones).
 */
export function PrintQueue({ station }: { station: PackingStation }) {
  const toast = useToast();
  const { isSupervisor } = useSession();
  const here = useDesk() === station.code;
  const log = useLoad(() => api<PrintJobState[]>('GET', `/api/wms/stations/${station.code}/print-log`), [station.code], 5_000);
  const desks = useDeskPrinters();
  usePrintsChanged(() => {
    void log.reload();
    void desks.reload();
  });
  const status = desks.data?.find((d) => d.code === station.code);
  const requeue = useRequeue();
  const [editing, setEditing] = useState(false);
  const onPhone = phone();
  const browserPrinted = station.labelPrinter.type === 'browser' || station.documentPrinter.type === 'browser';

  // a print agent's document is saved by hand ("İndir ve yazdırıldı say")
  const download = async (state: PrintJobState) => {
    try {
      const job = (await api<PrintJob[]>('GET', `/api/wms/stations/${station.code}/print-jobs`)).find((j) => j.id === state.id);
      if (!job || job.payload.encoding === 'object') throw new Error('Bu iş artık beklemiyor');
      const bytes = job.payload.encoding === 'base64' ? Uint8Array.from(atob(job.payload.data), (c) => c.charCodeAt(0)) : new TextEncoder().encode(job.payload.data);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([bytes], { type: job.format === 'PDF' ? 'application/pdf' : 'text/plain' }));
      link.download = `${job.kind}-${state.orderNumber}.${job.format.toLowerCase()}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
      await api('POST', `/api/wms/print-jobs/${job.id}/ack`, { status: 'printed' });
      void log.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const testLabel = async () => {
    try {
      await printZpl(testLabelZpl('100x150', station.code), 'Deneme etiketi');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const jobs = [...(log.data ?? [])].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state]);
  const open = jobs.filter((j) => j.state !== 'printed');
  const printed = jobs.filter((j) => j.state === 'printed').slice(0, 5);
  return (
    <section className="card desk-printer">
      <div className="card-h">
        <Icon name="printer" />
        <h2>Masanın yazıcısı</h2>
        <span className="sub">{PRINTER_LABELS[station.labelPrinter.type]}</span>
      </div>
      <div className="card-b stack">
        {browserPrinted && (
          <div className="row">
            {here ? (
              <span className="chip ok">
                <Icon name="check" size={14} /> Bu bilgisayar basıyor
              </span>
            ) : status?.online ? (
              <span className="chip ok">
                <Icon name="check" size={14} /> Yazıcı bilgisayarı açık
              </span>
            ) : status?.designated ? (
              <span className="chip bad">
                <Icon name="alert" size={14} /> Yazıcı bilgisayarı çevrimdışı
              </span>
            ) : status ? (
              <span className="chip warn">
                <Icon name="alert" size={14} /> Yazıcı bilgisayarı seçilmedi
              </span>
            ) : null}
            {!here && status && (
              <span className="muted" style={{ fontSize: 13 }}>
                {status.designated
                  ? 'Etiketler masadaki bilgisayardan basılır; bu cihaz yalnızca listeler.'
                  : `Etiketler basılmıyor: yazıcı bağlı bilgisayarda “Bu bilgisayar ${station.code} masasının yazıcısı”nı açın.`}
              </span>
            )}
          </div>
        )}
        {browserPrinted && (!onPhone || isSupervisor) && <DeskSwitch station={station} status={status} />}
        {browserPrinted && !onPhone && <RotateSwitch size="100x150" label="Kargo etiketlerini yatay bas (90° döndür)" />}
        <div className="row">
          {!onPhone && (
            <button className="btn sm" onClick={() => void testLabel()} title="Ölçek ve hizayı kontrol etmek için bu bilgisayardan 100×150 mm deneme etiketi">
              <Icon name="printer" size={16} /> Deneme etiketi
            </button>
          )}
          {isSupervisor && (
            <button className="btn sm ghost" onClick={() => setEditing(true)}>
              Yazıcı ayarı
            </button>
          )}
        </div>
        {here && (
          <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
            Her baskıda pencere açılmasın diye bu bilgisayarda Chrome / Edge "--kiosk-printing" ile açılmalı ve termal yazıcı varsayılan yazıcı olmalı
            (docs/yazici-kurulumu.md).
          </p>
        )}
      </div>
      {open.length + printed.length > 0 && (
        <div className="table-wrap">
          <table className="t stack-sm desk-jobs">
            <tbody>
              {[...open, ...printed].map((j) => (
                <tr key={j.id} className={j.state}>
                  <td>
                    <b className="mono">{j.orderNumber}</b>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {DOCUMENT_KIND_LABELS[j.kind]} · {j.format}
                    </div>
                    {j.state === 'failed' && j.error && <div className="err">{j.error}</div>}
                  </td>
                  <td className="num">
                    <span className="print-state">
                      {j.state === 'waiting' && j.printerType === 'agent' ? (
                        <button className="btn sm" onClick={() => void download(j)}>
                          İndir ve yazdırıldı say
                        </button>
                      ) : (
                        <PrintStateChip job={j} />
                      )}
                      {j.state === 'printed' && j.reprintable && (
                        <button className="btn sm ghost" onClick={() => void requeue(j)} title="Masanın yazıcısından bir kez daha basar">
                          Yeniden bas
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <StationPrinterDialog station={station} onClose={() => setEditing(false)} />}
    </section>
  );
}

/** Where the desk's labels and documents print; supervisors set it once per desk. */
function StationPrinterDialog({ station, onClose }: { station: PackingStation; onClose: () => void }) {
  const toast = useToast();
  const [type, setType] = useState<PrinterTarget['type']>(station.labelPrinter.type);
  const [host, setHost] = useState(station.labelPrinter.type === 'raw_tcp' ? station.labelPrinter.host : '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const label: PrinterTarget = type === 'raw_tcp' ? { type, host: host.trim() } : { type };
      await api('PUT', `/api/wms/stations/${station.code}`, {
        warehouseId: station.warehouseId,
        name: station.name,
        locationId: station.locationId,
        labelPrinter: label,
        // PDFs (e-İrsaliye) cannot go to a ZPL network printer
        documentPrinter: type === 'raw_tcp' ? { type: 'agent' } : label,
        isActive: station.isActive,
      });
      toast('Yazıcı ayarı kaydedildi', 'ok');
      window.location.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Masa ${station.code} · yazıcı`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || (type === 'raw_tcp' && !host.trim())} onClick={() => void save()}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="stack">
        {(Object.keys(PRINTER_LABELS) as PrinterTarget['type'][]).map((t) => (
          <label key={t} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input type="radio" name="printer" checked={type === t} onChange={() => setType(t)} />
            <span>
              <b>{PRINTER_LABELS[t]}</b>
              <br />
              <small className="muted">{PRINTER_HELP[t]}</small>
            </span>
          </label>
        ))}
        {type === 'raw_tcp' && (
          <div className="field">
            <label htmlFor="printer-host">Yazıcının IP adresi</label>
            <input id="printer-host" className="input mono" placeholder="192.168.1.50" value={host} onChange={(e) => setHost(e.target.value)} style={{ maxWidth: 220 }} />
          </div>
        )}
      </div>
    </Modal>
  );
}
